/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { LayoutDashboard, BarChart3, Activity, Zap, FileText, Monitor, Smartphone } from 'lucide-react';
import { DashboardPreview } from '../visuals/DashboardPreview';
import { PhoneMockup } from '../../HeroAnimation';

export const Dashboard = () => (
  <div className="space-y-12">
    <section>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-medium">
            The Dashboard is your central command center for all merchant and enterprise activity within the GhostPay ecosystem. It provides a real-time, high-level overview of your financial operations across all devices.
        </p>
    </section>

    <section className="space-y-8">
        <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">Interface Overview</h3>
        
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 items-start">
            {/* Desktop View */}
            <div className="xl:col-span-3 space-y-4">
                <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400 mb-2">
                    <Monitor size={16} />
                    <span className="text-xs font-bold uppercase tracking-widest">Desktop View</span>
                </div>
                <DashboardPreview />
                <p className="text-sm text-stone-500 dark:text-stone-400 mt-4 leading-relaxed">
                    The desktop interface is optimized for management and analytics. It features a comprehensive metrics grid, detailed revenue charting, and a dense activity feed for monitoring high-volume transaction flows.
                </p>
            </div>

            {/* Mobile View */}
            <div className="xl:col-span-2 space-y-4 flex flex-col items-center xl:items-start">
                <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400 mb-2 self-start xl:self-auto">
                    <Smartphone size={16} />
                    <span className="text-xs font-bold uppercase tracking-widest">Mobile View</span>
                </div>
                <div className="scale-[0.8] origin-top">
                    <PhoneMockup />
                </div>
                <p className="text-sm text-stone-500 dark:text-stone-400 mt-[-80px] leading-relaxed max-w-xs text-center xl:text-left">
                    The mobile experience focuses on "On-the-Go" actions: quick balance checks, POS notifications, and urgent approvals.
                </p>
            </div>
        </div>
    </section>
    
    <div className="border-t border-stone-200 dark:border-white/10 pt-8">
        <h4 className="text-xl font-serif font-bold text-stone-900 dark:text-white mb-6">Key Components</h4>
        <div className="grid md:grid-cols-2 gap-4">
            {[
                { icon: LayoutDashboard, title: "Metric Cards", desc: "At-a-glance KPIs including Total Revenue, Active Clients, Transaction Volume, and Net Profit." },
                { icon: BarChart3, title: "Revenue Analytics", desc: "A visual representation of your revenue over time. Hover for detailed values." },
                { icon: Activity, title: "Activity Feed", desc: "A live feed of the latest transactions, including payments, escrow actions, and deposits." },
                { icon: Zap, title: "Quick Actions", desc: "Shortcuts to common operations: send payments, create invoices, or start escrow." },
                { icon: FileText, title: "Reports", desc: "Create and download detailed financial reports in PDF or CSV format." }
            ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/10">
                    <div className="mt-1 text-nobel-gold flex-shrink-0">
                        <item.icon size={20} />
                    </div>
                    <div>
                        <h5 className="font-bold text-stone-900 dark:text-white text-sm mb-1">{item.title}</h5>
                        <p className="text-stone-600 dark:text-stone-400 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                </div>
            ))}
        </div>
    </div>
  </div>
);